'use client';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import type { Market } from '@//config/domain-locale-map';
import { googleMapsLoader } from '@//utils/google/googleMapsLoader';
import styles from './StaticMap.module.scss';

interface StaticMapProps {
  latitude: number;
  longitude: number;
  market: Market;
  zoom?: number;
  className?: string;
}

const StaticMap = ({ latitude, longitude, market, zoom = 10, className }: StaticMapProps) => {
  const t = useTranslations('components.staticMap');
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markerInstanceRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadMap = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (!mapRef.current) return;

        const { Map } = await googleMapsLoader.importLibrary(market, 'maps');
        const { AdvancedMarkerElement } = await googleMapsLoader.importLibrary(market, 'marker');

        if (mapInstanceRef.current) {
          mapInstanceRef.current = null;
        }
        if (markerInstanceRef.current) {
          markerInstanceRef.current = null;
        }

        mapInstanceRef.current = new Map(mapRef.current, {
          center: { lat: latitude, lng: longitude },
          zoom,
          disableDefaultUI: true,
          gestureHandling: 'none',
          zoomControl: false,
          scrollwheel: false,
          disableDoubleClickZoom: true,
          draggable: false,
          mapId: 'WAYKE_STATIC_MAP',
        });

        markerInstanceRef.current = new AdvancedMarkerElement({
          map: mapInstanceRef.current,
          position: { lat: latitude, lng: longitude },
          title: t('markerTitle'),
        });

        setIsLoading(false);
      } catch (err) {
        console.error('Error loading Google Maps:', err);
        setError(t('error'));
        setIsLoading(false);
      }
    };

    loadMap();

    return () => {
      markerInstanceRef.current = null;
      mapInstanceRef.current = null;
    };
  }, [latitude, longitude, market, zoom]);

  return (
    <div className={clsx(styles.mapWrapper, className)}>
      <div ref={mapRef} className={styles.map} />
      {isLoading && <span className={styles.message}>{t('loading')}</span>}
      {error && <span className={styles.message}>{error}</span>}
    </div>
  );
};

export default StaticMap;
